import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription, timer } from "rxjs";
import { finalize, switchMap, take, tap } from "rxjs/operators";
import { AuthService } from "../../services/auth.service";
import { PricingAmountLabelParts, PricingBillingPeriod, PricingPlan, PricingService } from "../../../shared/services/pricing.service";
import { environment } from "src/environments/environment";

@Component({
    selector: "app-checkout-page",
    templateUrl: "./checkout.component.html",
    styleUrls: ["./checkout.component.scss"],
})
export class CheckoutPageComponent implements OnInit, OnDestroy {
    planId = "trial";
    plan: PricingPlan | null = null;
    planLoading = true;
    /** Aligné sur la query `billing` (défaut annuel). */
    billing: PricingBillingPeriod = "yearly";
    userEmail: string | null = null;

    /** Compte à rebours avant redirection (null = overlay masqué). */
    appRedirectCountdown: number | null = null;

    private routeSub?: Subscription;
    private redirectSub?: Subscription;

    constructor(
        private readonly route: ActivatedRoute,
        readonly auth: AuthService,
        private readonly router: Router,
        private readonly pricing: PricingService,
    ) {
        this.userEmail = this.auth.currentAuth?.contact?.email ?? null;
    }

    ngOnInit(): void {
        this.routeSub = this.route.queryParamMap
            .pipe(
                tap(() => {
                    this.planLoading = true;
                    this.plan = null;
                    this.clearAppRedirectCountdown();
                }),
                switchMap((params) => {
                    this.planId = params.get("plan") || "trial";
                    this.billing = params.get("billing") === "monthly" ? "monthly" : "yearly";
                    return this.pricing.getPlanById(this.planId).pipe(
                        finalize(() => {
                            this.planLoading = false;
                        }),
                    );
                }),
            )
            .subscribe((plan) => {
                this.plan = plan;
            });
    }

    ngOnDestroy(): void {
        this.routeSub?.unsubscribe();
        this.clearAppRedirectCountdown();
    }

    onPaymentConfirmed(): void {
        this.startAppRedirectCountdown();
    }

    effectiveCheckoutAmountMga(plan: PricingPlan): number {
        return this.pricing.effectiveCheckoutAmountMga(plan, this.billing);
    }

    checkoutPriceParts(plan: PricingPlan): PricingAmountLabelParts | null {
        return this.pricing.amountLabelPartsForPlan(plan, this.billing);
    }

    checkoutPriceLabel(plan: PricingPlan): string {
        return this.pricing.amountLabelForPlan(plan, this.billing);
    }

    showYearlyBillingNote(plan: PricingPlan): boolean {
        return this.billing === "yearly" && this.pricing.referenceMonthlyMga(plan) != null;
    }

    /** Plan sans paiement en ligne : afficher le bouton puis le compte à rebours au clic. */
    showFreePlanStartButton(plan: PricingPlan): boolean {
        if (plan.ctaHref) {
            return false;
        }
        return this.effectiveCheckoutAmountMga(plan) <= 0;
    }

    onStartWithoutPayment(): void {
        this.startAppRedirectCountdown();
    }

    requiresOnlinePayment(plan: PricingPlan): boolean {
        return this.effectiveCheckoutAmountMga(plan) > 0;
    }

    private startAppRedirectCountdown(): void {
        this.clearAppRedirectCountdown();
        this.appRedirectCountdown = 5;
        this.redirectSub = timer(0, 1000)
            .pipe(take(6))
            .subscribe({
                next: (tick) => {
                    if (tick < 5) {
                        this.appRedirectCountdown = 5 - tick;
                    }
                },
                complete: () => {
                    this.redirectToApp();
                },
            });
    }

    private clearAppRedirectCountdown(): void {
        this.redirectSub?.unsubscribe();
        this.redirectSub = undefined;
        this.appRedirectCountdown = null;
    }

    private redirectToApp(): void {
        window.location.assign(environment.appUrl);
    }

    logout(): void {
        this.auth.logout();
        void this.router.navigate(["/"]);
    }
}
